import { useEffect, useState } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { BarChart3, TrendingUp, Send, Eye, MessageCircle, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = ["#002FA7", "#FF5722", "#16A34A", "#F59E0B", "#8B5CF6", "#06B6D4"];

const SOURCE_LABELS = {
  ai_generated: "AI Generated",
  csv_upload: "CSV Upload",
  domain_scrape: "Domain Scraper",
  csv_enriched: "CSV Enriched",
  lookalike: "Lookalike",
  trigger_funding: "Trigger: Funding",
  trigger_hiring: "Trigger: Hiring",
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/stats/analytics?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">— Performance</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Analytics</h1>
          <p className="text-[#5C5C66] mt-2">Track outreach performance and lead generation trends.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 rounded-lg border-[#EAEAE3]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" size="icon" className="rounded-lg border-[#EAEAE3]">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Time Series Chart */}
      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft mb-5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tighter">Email Performance Over Time</div>
            <div className="text-xs text-[#5C5C66]">Open rate & reply rate by day</div>
          </div>
        </div>
        {data?.time_series?.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.time_series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAEAE3" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} unit="%" />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #EAEAE3", fontFamily: "IBM Plex Mono", fontSize: 12 }}
                formatter={(v) => [`${v}%`]}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
              <Line type="monotone" dataKey="open_rate" stroke="#002FA7" strokeWidth={2} dot={false} name="Open Rate" />
              <Line type="monotone" dataKey="reply_rate" stroke="#FF5722" strokeWidth={2} dot={false} name="Reply Rate" />
              <Line type="monotone" dataKey="click_rate" stroke="#16A34A" strokeWidth={2} dot={false} name="Click Rate" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-48 flex items-center justify-center text-[#5C5C66] text-sm">
            {loading ? "Loading…" : "No sent email data yet. Send some outreach to see trends."}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Campaign Performance */}
        <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-[#F4F4EE] text-[#0A0A0F] flex items-center justify-center">
              <BarChart3 className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tighter">Campaign Performance</div>
              <div className="text-xs text-[#5C5C66]">Reply rate by campaign</div>
            </div>
          </div>
          {data?.campaign_performance?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.campaign_performance} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAEAE3" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} angle={-30} textAnchor="end" />
                <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} unit="%" />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #EAEAE3", fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  formatter={(v) => [`${v}%`]}
                />
                <Bar dataKey="reply_rate" fill="#002FA7" radius={[6, 6, 0, 0]} name="Reply Rate" />
                <Bar dataKey="open_rate" fill="#002FA7" fillOpacity={0.25} radius={[6, 6, 0, 0]} name="Open Rate" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-[#5C5C66] text-sm">No campaign data yet.</div>
          )}
        </div>

        {/* Lead Sources Pie */}
        <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-[#F4F4EE] text-[#0A0A0F] flex items-center justify-center">
              <Send className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tighter">Lead Sources</div>
              <div className="text-xs text-[#5C5C66]">Where your leads come from</div>
            </div>
          </div>
          {data?.lead_sources?.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="55%" height={200}>
                <PieChart>
                  <Pie data={data.lead_sources} cx="50%" cy="50%" outerRadius={80} dataKey="count" nameKey="source">
                    {data.lead_sources.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #EAEAE3", fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {data.lead_sources.map((s, i) => (
                  <div key={s.source} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-[#5C5C66] font-mono">{SOURCE_LABELS[s.source] || s.source}</span>
                    </div>
                    <span className="font-mono font-bold">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-[#5C5C66] text-sm">No lead source data yet.</div>
          )}
        </div>
      </div>

      {/* Campaign table */}
      {data?.campaign_performance?.length > 0 && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
          <div className="p-5 border-b border-[#EAEAE3]">
            <div className="font-display text-lg font-bold tracking-tighter">Campaign Breakdown</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#EAEAE3] bg-[#FAFAF7]">
                  {["Campaign", "Leads", "Sent", "Opened", "Replied", "Open %", "Reply %"].map((h) => (
                    <th key={h} className="px-5 py-3 text-left label-mono">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.campaign_performance.map((c, i) => (
                  <tr key={i} className="border-b border-[#EAEAE3] last:border-0 hover:bg-[#FAFAF7] transition-colors">
                    <td className="px-5 py-3 font-medium">{c.name}</td>
                    <td className="px-5 py-3 font-mono">{c.leads}</td>
                    <td className="px-5 py-3 font-mono">{c.sent}</td>
                    <td className="px-5 py-3 font-mono">{c.opened}</td>
                    <td className="px-5 py-3 font-mono">{c.replied}</td>
                    <td className="px-5 py-3 font-mono text-[#002FA7]">{c.open_rate}%</td>
                    <td className="px-5 py-3 font-mono text-[#FF5722]">{c.reply_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
